import MapBox from './modules/map'
import authClient from './modules/oktaAuth'

import SantryIO from './modules/SantryIO'
SantryIO.initialize()

import { getAirportsFeatureCollection, addAirportsToMap, updateAirportIconCode } from './services/airport'
import { addMapImages } from './services/map'
import { DetailsPanel } from './components/DetailsPanel/DetailsPanel'
import { AirportPins } from './components/AirportPins/AirportPins'
import { SearchBar } from './components/SearchBar/SearchBar'
import { MyRoutes } from './components/MyRoutes/MyRoutes'
import { MyAirports } from './components/MyAirports/MyAirports'
import { MyFleet } from './components/MyFleet/MyFleet'
import { BriefBuilder } from './components/BriefBuilder/BriefBuilder'
import { EventFilter } from './components/EventFilter/EventFilter'
import { Compare } from './components/Compare/Compare'
import { EventPanel } from './components/EventPanel/EventPanel'
import { SideNavigation } from './components/SideNavigation/SideNavigation'
import { EventDetailsPanel } from './components/EventDetailsPanel/EventDetailsPanel'
import { TutorialComponent } from './components/Tutorial/Tutorial'
import { AlertManagementModal } from './components/AlertManagementModal/AlertManagementModal'
import { Sprite } from './components/Sprite/Sprite'
import { AlertNotification } from './components/AlertNotification/AlertNotification'
import { AlertBarBottom } from './components/AlertBarBottom/AlertBarBottom'
import { InsightsAndReports } from './components/InsightsAndReports/InsightsAndReports'
import { OrganisationSettingsModal } from './sections/dashboard/components/OrganisationSettingsModal/OrganisationSettingsModal'

import './resources/scss/theme/_theme-dark.scss'
import './resources/scss/main.scss'
import './resources/scss/module/airport/_airport.scss'
import './resources/scss/module/user-shared/_user-shared.scss'
import './resources/scss/components/_map.scss'
import 'mapbox-gl/dist/mapbox-gl.css'

import ClientPortalAPIService from './services/api/ClientPortal/ApiService'
import CovidAPI from './services/api/Covid/ApiService'
import AvsecAlerts from './modules/avsecAlerts'
import InternationalSOSAlerts from './modules/InternationalSOSAlerts'
import Airport from './modules/airport'
import { convertDMS } from './services/utils'
import axios from 'axios'
import SecurityCMSAPIService from './services/api/SecurityCMS/ApiService'
import MixPanelService from './services/MixPanelService'
import TailsModule from './modules/tails'
import BottomNavigationService from './services/BottomNavigation'
import AeroSpace from './modules/aerospace'
import RoutesModule from './modules/routes'
import User from './modules/user'
import screenfull from 'screenfull'
import { FIR_DETAILS } from './data/fir-details'
import ModifyAlertsLocation from './modules/modifyAlertsLocation'
import * as turf from '@turf/turf'

MixPanelService.track('Page View')
MixPanelService.AddUserData()

const initialLoadingChecks = {
  isAuthenticated: false,
  hasPermissionToAccess: false,
  hasLoadedAirportsBasicData: false
}

const accessToken = authClient.getAccessToken()
axios.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`

// If any API returns not authorized error, then sign out user
axios.interceptors.response.use(undefined, function (error) {
  if (error.response !== undefined && error.response.status === 401) {
    console.log('An API is not authorized, signing out the user.')
    try {
      window.sessionStorage.removeItem('alert-management-session-shown')
    } catch (e) { /* ignore */ }
    authClient.signOut()
  } else {
    return Promise.reject(error)
  }
})
authClient.start()
authClient.authStateManager.subscribe((authState) => {
  if (!authState.isAuthenticated) {
    window.location.href = '/'
  } else {
    initialLoadingChecks.isAuthenticated = true
    triggerInitialLoadingChecks()
  }
})

const triggerInitialLoadingChecks = () => {
  User.fetchUserPermissions()
  User.getUserPermissions().then(permissions => {
    if (permissions.indexOf('basic') > -1) {
      initialLoadingChecks.hasPermissionToAccess = true
    } else {
      console.log('User doesn\'t have the basic permission to view this section, redirecting...')
      window.location.href = '/'
    }
  }).then(() => {
    ClientPortalAPIService.getClientDetails().then(clientDetails => {
      OrganisationSettingsModal.$data.clientName = clientDetails.clientName
  
      if (clientDetails.settings.severity) {
          OrganisationSettingsModal.$data.alertSeverities = clientDetails.settings.severity
      } else {
          OrganisationSettingsModal.$data.alertSeverities = []
      }
  
      let existingClientNumber = clientDetails.settings.clientEmergencyNumber
      if (existingClientNumber) {
          OrganisationSettingsModal.$data.selectedPhoneCodePrefix = existingClientNumber.substr(0, existingClientNumber.indexOf(' '))
          OrganisationSettingsModal.$data.emergencyNumber = existingClientNumber.substr(existingClientNumber.indexOf(' ') + 1)
      }
    })

    ClientPortalAPIService.getAirports().then(airports => {
      // With default values
      const airportsFeatureCollection = getAirportsFeatureCollection(airports, true, false, false, true, true)
      addAirportsToMap(airportsFeatureCollection)

      SearchBar.$data.airports = airports
      MyRoutes.$data.airports = airports
      MyAirports.$data.airportsData = airports

      MyAirports.initialize()

      initialLoadingChecks.hasLoadedAirportsBasicData = true
      AvsecAlerts.initialize(airports)
      AeroSpace.initialize(airports)
      document.getElementsByClassName('app-template')[0].classList.add('is-loading-comstat')
      Compare.initialize(airports).then(() => {
        document.getElementsByClassName('app-template')[0].classList.remove('is-loading-comstat')
      })

      AeroSpace.getModuleWarningsAndRestrictions().then(restrictions => {
        DetailsPanel.$data.warningsAndRestrictions = restrictions
      })

      checkInitialLoading()

      // This needs to be as last as it still has a blocking sync heaving route deciduing calculations
      MyRoutes.initialize()
      InsightsAndReports.initialize()
    })
  })
}

User.getUser().then(response => {
  MixPanelService.bindUserModel(response)
  MixPanelService.track('Page View')
})

// Observe if user's airport code preference has changed and update the airport icon codes accordingly
window.setInterval(() => { updateAirportIconCode(User.userSettings.airportCode) }, 2500)

InternationalSOSAlerts.initialize()
TailsModule.initialize()
Airport.initialize()
EventPanel.initialize()

SearchBar.$data.enableAviationSearchAndFunctionalities = true

// Initial resize function call to adjust the map to the screen
MapBox.map.resize()

// Disable map rotation using right click + drag
MapBox.map.dragRotate.disable()

// Disable map rotation using touch rotation gesture
MapBox.map.touchZoomRotate.disableRotation()

// Generic map event listeners
document.getElementById('map-navigation-center').addEventListener('click', () => {
  MapBox.map.setZoom(2)
  MapBox.map.setCenter([30, 15.5])
})

document.getElementById('map-navigation-zoom-in').addEventListener('click', () => {
  MapBox.map.zoomIn({ duration: 500 })
})

document.getElementById('map-navigation-zoom-out').addEventListener('click', () => {
  MapBox.map.zoomOut({ duration: 500 })
})

if (screenfull.isEnabled) {
  document.getElementById('map-navigation-full-screen').addEventListener('click', () => {
    screenfull.toggle()
  })
} else {
  document.getElementById('map-navigation-full-screen').remove()
}

const mapLatLngInfo = document.getElementById('map-lat-lng-info')
MapBox.map.on('mousemove', (e) => {
  mapLatLngInfo.innerHTML = convertDMS(e.lngLat.lat, e.lngLat.lng)
})

// For button click effect - TODO need to make this work with dynamic elements
const buttonsForClickEffect = document.getElementsByClassName('button--feedback')
const toggleClickedClass = function () {
  this.classList.add('show-click-effect')
  setTimeout(() => {
    this.classList.remove('show-click-effect')
  }, 600)
}

// Temp on for review
Array.from(buttonsForClickEffect).forEach(function (element) {
  element.addEventListener('click', toggleClickedClass)
})

MapBox.map.on('load', () => {
  addMapImages(MapBox.map)

  MapBox.map.on('click', (e) => {
    const features = MapBox.map.queryRenderedFeatures(e.point)

    const airportClicked = features.find(feature => feature.layer.id === 'airports')
    const internationalAosAlertsClicked = features.find(feature => feature.layer.id === 'international-sos-alerts')
    const avsecAlertsClicked = features.find(feature => feature.layer.id === 'avsec-alerts')
    const avsecAlertsBufferClicked = features.find(feature => feature.layer.id === 'avsec-alerts-buffer')
    const firLayerClicked = features.find(feature => feature.layer.id === 'fir-fill')

    if (airportClicked) {
      const airportData = airportClicked.properties
      if (EventDetailsPanel.isPanelOpen) {
        EventDetailsPanel.hidePanel()

        const timeout = window.setTimeout(() => {
          DetailsPanel.showDetailsPanel(airportData)
          window.clearTimeout(timeout)
        }, 500)
      } else {
        DetailsPanel.showDetailsPanel(airportData)
      }
    } else if (avsecAlertsClicked) {
      DetailsPanel.hideDetailsPanel()
      const alertIconProperties = avsecAlertsClicked?.properties
      if (alertIconProperties.type === 'location') {
        AvsecAlerts.showAlert(alertIconProperties.NodeId, 'location')
      } else {
        let allAvsecAlertsOfTheCountry = AvsecAlerts.getAffectedCountryAvsecAlerts(alertIconProperties.isoCode2)
        EventDetailsPanel.showAviationCountryAlerts(allAvsecAlertsOfTheCountry, alertIconProperties.isoCode2)
      }
    } else if (internationalAosAlertsClicked !== undefined) {
      DetailsPanel.hideDetailsPanel()
      const alertID = internationalAosAlertsClicked.properties.Id
      const alertDetails = InternationalSOSAlerts.getAlertDetails(alertID)
      let alertLngLat = []
      if (internationalAosAlertsClicked.geometry.type === 'Point') {
        alertLngLat = internationalAosAlertsClicked.geometry.coordinates
      } else if (internationalAosAlertsClicked.geometry.type === 'MultiPoint') {
        // find the closest point to the clicked point
        const closestPoint = internationalAosAlertsClicked.geometry.coordinates.reduce((prev, curr) => {
          const prevDistance = turf.distance(turf.point(prev), turf.point([e.lngLat.lng, e.lngLat.lat]))
          const currDistance = turf.distance(turf.point(curr), turf.point([e.lngLat.lng, e.lngLat.lat]))
          return prevDistance < currDistance ? prev : curr
        })
        alertLngLat = closestPoint
      }
      alertLngLat = internationalAosAlertsClicked?.lngLat || alertLngLat
      if (alertDetails !== undefined) {
        EventDetailsPanel.showInternationalSOSAlert(alertDetails, alertLngLat)
        MixPanelService.track('International SOS Alert Clicked', { 
          Id: alertDetails.Id,
          Title: alertDetails.Title,
          Type: alertDetails.Service
         })
      } 
    } else if (avsecAlertsBufferClicked) {
      DetailsPanel.hideDetailsPanel()
      const alertIconProperties = avsecAlertsBufferClicked.properties
      if (alertIconProperties.type === 'location') {
        AvsecAlerts.showAlert(alertIconProperties.NodeId, 'location')
      } else {
        let allAvsecAlertsOfTheCountry = AvsecAlerts.getAffectedCountryAvsecAlerts(alertIconProperties.isoCode2)
        EventDetailsPanel.showAviationCountryAlerts(allAvsecAlertsOfTheCountry, alertIconProperties.isoCode2)
      }
    }
    else if (firLayerClicked) {
      const info = firLayerClicked?.properties
      const id = firLayerClicked?.id
      if (FIR_DETAILS[info.CODE] !== undefined) {
        AeroSpace.selectFIR(info.CODE, FIR_DETAILS[info.CODE].controllingCountryCode, FIR_DETAILS[info.CODE].name, FIR_DETAILS[info.CODE].countries, info.centlong, info.centlat, id)
      } else {
        window.alert('Could not find airspace information for this FIR layer')
      }
    }

  })

  ModifyAlertsLocation.initialize()
})

CovidAPI.getCovidRestrictions().then((covidRestrictions) => {
  DetailsPanel.$data.covidRestrictions = covidRestrictions
  MyRoutes.$data.covidRestrictions = covidRestrictions
})

SecurityCMSAPIService.getCountriesWeaponaryData().then(countriesWeaponaryData => {
  if (countriesWeaponaryData) {
    DetailsPanel.$data.countriesWeaponaryData = countriesWeaponaryData
    RoutesModule.countriesWeaponaryData = countriesWeaponaryData
  }
})

BottomNavigationService.initialize()

const checkInitialLoading = () => {
  if (initialLoadingChecks.isAuthenticated && initialLoadingChecks.hasPermissionToAccess && initialLoadingChecks.hasLoadedAirportsBasicData) {
    // Hide loading screen
    document.getElementById('app-loading-screen').classList.add('app-has-loaded')

    window.clearInterval(initialLoadingCheckInterval)
  }
}

const initialLoadingCheckInterval = window.setInterval(() => {
  checkInitialLoading()
}, 50)